import React, { useState } from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  TextField,
  Button,
  Grid,
  Typography,
  makeStyles,
  Paper,
  IconButton,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Box,
  Tooltip,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Opacity as ScienceIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  LocalFlorist as PlantIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
  Info as InfoIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    padding: 0,
    maxWidth: 900,
    width: '95%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderRadius: '12px 12px 0 0',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(3),
    overflowY: 'auto',
    flex: 1,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  sectionDivider: {
    marginBottom: theme.spacing(2),
  },
  input: {
    marginBottom: theme.spacing(2),
  },
  arrayContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  arrayItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  chipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  requiredField: {
    '& label': {
      '&::after': {
        content: '" *"',
        color: theme.palette.error.main,
      },
    },
  },
  helperText: {
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
}));

const HERBICIDE_CATEGORIES = [
  'Selective',
  'Non-selective',
  'Pre-emergent',
  'Post-emergent',
  'Surfactant',
  'Turf/Broadleaf',
  'Dye',
];

const CreateChemicalModal = ({ open, onClose, onSave }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    name: '',
    altName: '',
    description: '',
    herbicideDetails: {
      activeIngredient: '',
      formulation: '',
      targetWeeds: [''],
      targetLocation: [''],
      applicationMethod: [''],
      safetyPrecautions: '',
      epaRegistrationNumber: '',
      manufacturer: '',
      category: '',
      compatibleCrops: [''],
      reEntryInterval: '',
      preharvest_interval: '',
      msds_url: '',
      label_url: '',
    },
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e, path = null) => {
    const { name, value } = e.target;

    if (path) {
      setFormData((prev) => ({
        ...prev,
        herbicideDetails: {
          ...prev.herbicideDetails,
          [path]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[path || name]) {
      setErrors((prev) => ({ ...prev, [path || name]: null }));
    }
  };

  const handleArrayChange = (e, field, index) => {
    const newArray = [...formData.herbicideDetails[field]];
    newArray[index] = e.target.value;
    setFormData((prev) => ({
      ...prev,
      herbicideDetails: {
        ...prev.herbicideDetails,
        [field]: newArray,
      },
    }));
  };

  const addArrayField = (field) => {
    setFormData((prev) => ({
      ...prev,
      herbicideDetails: {
        ...prev.herbicideDetails,
        [field]: [...prev.herbicideDetails[field], ''],
      },
    }));
  };

  const removeArrayField = (field, index) => {
    const newArray = formData.herbicideDetails[field].filter((_, i) => i !== index);
    // Ensure at least one empty field remains
    const finalArray = newArray.length === 0 ? [''] : newArray;
    setFormData((prev) => ({
      ...prev,
      herbicideDetails: {
        ...prev.herbicideDetails,
        [field]: finalArray,
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Chemical name is required';
    }
    if (!formData.herbicideDetails.activeIngredient.trim()) {
      newErrors.activeIngredient = 'Active ingredient is required';
    }
    if (!formData.herbicideDetails.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.herbicideDetails.manufacturer.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Filter out empty array values
      const cleanedData = {
        ...formData,
        herbicideDetails: {
          ...formData.herbicideDetails,
          targetWeeds: formData.herbicideDetails.targetWeeds.filter(item => item.trim()),
          targetLocation: formData.herbicideDetails.targetLocation.filter(item => item.trim()),
          applicationMethod: formData.herbicideDetails.applicationMethod.filter(item => item.trim()),
          compatibleCrops: formData.herbicideDetails.compatibleCrops.filter(item => item.trim()),
        },
      };
      console.log('cleaned', cleanedData)
      onSave(cleanedData);
      // onClose();
    }
  };

  const renderArrayField = (field, label, icon) => {
    return (
      <div className={classes.arrayContainer}>
        <Typography variant="subtitle2" color="textSecondary">
          {icon} {label}
        </Typography>
        {formData.herbicideDetails[field].map((value, index) => (
          <div key={`${field}-${index}`} className={classes.arrayItem}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={value}
              onChange={(e) => handleArrayChange(e, field, index)}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            {formData.herbicideDetails[field].length > 1 && (
              <IconButton
                size="small"
                color="secondary"
                onClick={() => removeArrayField(field, index)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        ))}
        <Button
          startIcon={<AddIcon />}
          size="small"
          color="primary"
          onClick={() => addArrayField(field)}
          className={classes.addButton}
        >
          Add {label}
        </Button>
      </div>
    );
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Paper className={classes.paper}>
          <div className={classes.header}>
            <Typography variant="h5" className={classes.headerTitle}>
              <ScienceIcon />
              Create New Herbicide
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8, opacity: 0.9 }}>
              Add a new chemical to database
            </Typography>
          </div>

          <div className={classes.content}>
            {/* Basic Information Section */}
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionHeader}>
                <DescriptionIcon />
                Basic Information
              </Typography>
              <Divider className={classes.sectionDivider} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Chemical Name"
                    name="name"
                    fullWidth
                    variant="outlined"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    className={classes.requiredField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Alternative Name"
                    name="altName"
                    fullWidth
                    variant="outlined"
                    value={formData.altName}
                    onChange={handleChange}
                    helperText="Common or trade name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    helperText="Provide a detailed description of the herbicide"
                  />
                </Grid>
              </Grid>
            </div>

            {/* Chemical Properties Section */}
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionHeader}>
                <ScienceIcon />
                Chemical Properties
              </Typography>
              <Divider className={classes.sectionDivider} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Active Ingredient"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.activeIngredient}
                    onChange={(e) => handleChange(e, 'activeIngredient')}
                    error={!!errors.activeIngredient}
                    helperText={errors.activeIngredient || "e.g., Glyphosate, 2,4-D"}
                    className={classes.requiredField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Formulation"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.formulation}
                    onChange={(e) => handleChange(e, 'formulation')}
                    helperText="e.g., 41% concentrate, granular"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" error={!!errors.category}>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      value={formData.herbicideDetails.category}
                      onChange={(e) => handleChange(e, 'category')}
                      label="Category *"
                    >
                      <MenuItem value="">
                        <em>Select a category</em>
                      </MenuItem>
                      {HERBICIDE_CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="EPA Registration Number"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.epaRegistrationNumber}
                    onChange={(e) => handleChange(e, 'epaRegistrationNumber')}
                    helperText="Official EPA registration"
                  />
                </Grid>
              </Grid>
            </div>

            {/* Application Details Section */}
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionHeader}>
                <BuildIcon />
                Application Details
              </Typography>
              <Divider className={classes.sectionDivider} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderArrayField('targetWeeds', 'Target Weeds', <PlantIcon fontSize="small" />)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderArrayField('targetLocation', 'Target Locations', <LocationIcon fontSize="small" />)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderArrayField('applicationMethod', 'Application Methods', <BuildIcon fontSize="small" />)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderArrayField('compatibleCrops', 'Compatible Crops', <PlantIcon fontSize="small" />)}
                </Grid>
              </Grid>
            </div>

            {/* Safety & Compliance Section */}
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionHeader}>
                <WarningIcon />
                Safety & Compliance
              </Typography>
              <Divider className={classes.sectionDivider} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Safety Precautions"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                    value={formData.herbicideDetails.safetyPrecautions}
                    onChange={(e) => handleChange(e, 'safetyPrecautions')}
                    helperText="PPE requirements, handling instructions, etc."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Re-entry Interval"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.reEntryInterval}
                    onChange={(e) => handleChange(e, 'reEntryInterval')}
                    helperText="Time before safe re-entry (e.g., 4 hours)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pre-harvest Interval"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.preharvest_interval}
                    onChange={(e) => handleChange(e, 'preharvest_interval')}
                    helperText="Days before harvest (e.g., 30 days)"
                  />
                </Grid>
              </Grid>
            </div>

            {/* Manufacturer & Documentation Section */}
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionHeader}>
                <BusinessIcon />
                Manufacturer & Documentation
              </Typography>
              <Divider className={classes.sectionDivider} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Manufacturer"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.manufacturer}
                    onChange={(e) => handleChange(e, 'manufacturer')}
                    error={!!errors.manufacturer}
                    helperText={errors.manufacturer}
                    className={classes.requiredField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="MSDS URL"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.msds_url}
                    onChange={(e) => handleChange(e, 'msds_url')}
                    helperText="Link to Material Safety Data Sheet"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Label URL"
                    fullWidth
                    variant="outlined"
                    value={formData.herbicideDetails.label_url}
                    onChange={(e) => handleChange(e, 'label_url')}
                    helperText="Link to product label"
                  />
                </Grid>
              </Grid>
            </div>
          </div>

          <div className={classes.actions}>
            <Button
              onClick={onClose}
              variant="outlined"
              size="large"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
            >
              Create Herbicide
            </Button>
          </div>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default CreateChemicalModal;
